import { useState, useMemo, useEffect } from 'react';
import { useItems } from '../hooks/useItems';
import { useCategories } from '../hooks/useCategories';
import { useInventory } from '../hooks/useInventoryOperations';
import { inventoryService } from '../services/inventoryService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useJobs } from '../hooks/useJobs';

const AddInventoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { categories } = useCategories();
  const { jobs } = useJobs();
  const [categoryFilter, setCategoryFilter] = useState('');
  // Load all items without pagination for search
  const { items } = useItems(categoryFilter ? parseInt(categoryFilter) : null, 0, 10000);
  const { addInventory, loading, error } = useInventory();
  const [success, setSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const urlJobId = searchParams.get('job_id') || '';
  const [isNewOldStock, setIsNewOldStock] = useState(!!urlJobId);
  const [averagePrice, setAveragePrice] = useState(null);
  const [fetchingPrice, setFetchingPrice] = useState(false);

  const [formData, setFormData] = useState({
    item_id: '',
    quantity: '',
    unit_price: '',
    date_received: new Date().toISOString().split('T')[0],
    job_id: urlJobId,
    notes: ''
  });

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [items, searchTerm]);

  const handleItemSelect = (item) => {
    setFormData({ 
      ...formData, 
      item_id: item.id,
      unit_price: item.default_price ? item.default_price.toString() : '' // Load default price
    });
    setSearchTerm(item.name);
    setShowDropdown(false);
  };

  // Fetch average price when item changes and NOS is checked
  useEffect(() => {
    const fetchAveragePrice = async () => {
      if (formData.item_id && isNewOldStock) {
        setFetchingPrice(true);
        try {
          const result = await inventoryService.getAveragePrice(formData.item_id);
          if (result.average_price !== null) {
            setAveragePrice(result.average_price);
            setFormData(prev => ({ ...prev, unit_price: result.average_price.toFixed(2) }));
          } else {
            setAveragePrice(null);
            // Keep default price if no average available
          }
        } catch (err) {
          console.error('Failed to fetch average price:', err);
          setAveragePrice(null);
        } finally {
          setFetchingPrice(false);
        }
      }
    };

    fetchAveragePrice();
  }, [formData.item_id, isNewOldStock]);

  const handleNOSChange = (e) => {
    const checked = e.target.checked;
    setIsNewOldStock(checked);

    if (!checked) {
      // Reset price and job when unchecking NOS (preserve URL-provided job)
      setFormData(prev => ({ ...prev, unit_price: '', job_id: urlJobId }));
      setAveragePrice(null);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    if (!e.target.value) {
      setFormData({ ...formData, item_id: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const notes = isNewOldStock 
        ? `New-Old Stock (NOS) - Returned inventory. ${formData.notes}`.trim()
        : formData.notes;
      
      const dataToSubmit = {
        ...formData,
        item_id: parseInt(formData.item_id),
        quantity: parseInt(formData.quantity),
        unit_price: parseFloat(formData.unit_price),
        job_id: formData.job_id ? parseInt(formData.job_id) : undefined,
        notes
      };
      
      await addInventory(dataToSubmit);
      
      setSuccess(true);
      setFormData({
        item_id: '',
        quantity: '',
        unit_price: '',
        date_received: new Date().toISOString().split('T')[0],
        job_id: '',
        notes: ''
      });
      setSearchTerm('');
      setIsNewOldStock(false);
      setAveragePrice(null);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to add inventory:', err);
    }
  };

  const selectedItem = items.find(item => item.id === parseInt(formData.item_id));

  return (
    <div className="container py-4" style={{ maxWidth: '800px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Add Inventory</h2>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/inventory')}>
          Back to Inventory
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          Inventory added successfully!
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Category *</label>
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setFormData({ ...formData, item_id: '' });
                  setSearchTerm('');
                }}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Item *</label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Search for an item..."
                  required
                  autoComplete="off"
                />
                {showDropdown && filteredItems.length > 0 && (
                  <ul className="list-group position-absolute w-100" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                    {filteredItems.map(item => (
                      <li
                        key={item.id}
                        className="list-group-item list-group-item-action"
                        onClick={() => handleItemSelect(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div><strong>{item.name}</strong></div>
                        <small className="text-muted">{item.category?.name} - {item.unit_of_measure}</small>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {selectedItem && (
              <div className="alert alert-info">
                <strong>Unit of Measure:</strong> {selectedItem.unit_of_measure}
              </div>
            )}

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="newOldStock"
                  checked={isNewOldStock}
                  onChange={handleNOSChange}
                  disabled={!formData.item_id}
                />
                <label className="form-check-label" htmlFor="newOldStock">
                  <strong>New-Old Stock (NOS)</strong>
                </label>
              </div>
              <small className="text-muted">
                Check this for returned inventory from jobs. Price will be automatically set to the average of all previous purchases for this item. If no price history exists, manual entry is required.
              </small>
              
              {/* Job selector - only show when NOS is checked */}
              {isNewOldStock && (
                <div className="mt-3">
                  <label className="form-label">Return From Job (Optional)</label>
                  <select
                    className="form-select"
                    value={formData.job_id}
                    onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                  >
                    <option value="">Select job (optional)...</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.name}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    Track which job this inventory was returned from
                  </small>
                </div>
              )}
              
              {isNewOldStock && fetchingPrice && (
                <div className="mt-2">
                  <small className="text-info">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Calculating average price...
                  </small>
                </div>
              )}
              {isNewOldStock && !fetchingPrice && averagePrice !== null && (
                <div className="alert alert-success mt-2 mb-0">
                  <small>
                    <i className="bi bi-check-circle me-2"></i>
                    Average price calculated: ${averagePrice.toFixed(2)}
                  </small>
                </div>
              )}
              {isNewOldStock && !fetchingPrice && averagePrice === null && formData.item_id && (
                <div className="alert alert-warning mt-2 mb-0">
                  <small>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    No price history found for this item. Please enter a price manually.
                  </small>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Quantity *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  min="1"
                  step="1"
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Unit Price ($) *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  min="0.01"
                  step="0.01"
                  required
                  disabled={isNewOldStock && averagePrice !== null}
                />
                {isNewOldStock && averagePrice !== null && (
                  <small className="text-muted">
                    Price locked to average (New-Old Stock)
                  </small>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Date Received *</label>
              <input
                type="date"
                className="form-control"
                value={formData.date_received}
                onChange={(e) => setFormData({ ...formData, date_received: e.target.value })}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Purchase order number, supplier, etc."
              />
            </div>

            {formData.quantity && formData.unit_price && (
              <div className="alert alert-secondary">
                <strong>Total Value:</strong> ${(parseFloat(formData.quantity) * parseFloat(formData.unit_price)).toFixed(2)}
              </div>
            )}

            <div className="d-grid gap-2">
              <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Adding Inventory...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Inventory
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-4 p-3 bg-light rounded">
        <h6 className="text-muted mb-2">What happens when you add inventory?</h6>
        <ul className="mb-0 small">
          <li>A new lot is created with the purchase price you enter</li>
          <li>A CREDIT transaction is recorded in the ledger</li>
          <li>This lot will be used first (FIFO) when inventory is removed</li>
        </ul>
      </div>
    </div>
  );
};

export default AddInventoryPage;
